import { useState } from 'react';
import { Link } from 'react-router-dom';
import { depthValue } from '../lib/depth';

// A model at a glance (the stat strip) and in full (the specs table). Numbers are compact
// where they must fit a phone, with the exact value on hover.

export function compact(n, unit = '') {
  if (n === null || n === undefined || Number.isNaN(n)) return '–';
  const abs = Math.abs(n);
  const fmt = (v, s) => `${v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2)}${s}`;
  if (abs >= 1e12) return fmt(n / 1e12, 'T') + unit;
  if (abs >= 1e9) return fmt(n / 1e9, 'B') + unit;
  if (abs >= 1e6) return fmt(n / 1e6, 'M') + unit;
  if (abs >= 1e3) return fmt(n / 1e3, 'K') + unit;
  return `${n}${unit}`;
}

export function bytes(n) {
  if (!n) return '0 B';
  if (n >= 1e12) return `${(n / 1e12).toFixed(2)} TB`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)} KB`;
  return `${n} B`;
}

function ago(iso) {
  if (!iso) return '–';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 86400 * 60) return `${Math.round(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toLowerCase();
}

export function StatStrip({ checkpoint }) {
  const stats = [
    ['parameters', checkpoint.parameters ? compact(checkpoint.parameters) : '–', checkpoint.parameters?.toLocaleString()],
    ['text seen', bytes(checkpoint.bytes_seen), `${(checkpoint.bytes_seen || 0).toLocaleString()} bytes`],
    ['tokens', checkpoint.tokens_seen ? compact(checkpoint.tokens_seen) : '–', checkpoint.tokens_seen?.toLocaleString()],
    ['file', bytes(checkpoint.file_size_bytes), `${(checkpoint.file_size_bytes || 0).toLocaleString()} bytes`],
    ['updated', ago(checkpoint.updated_at || checkpoint.created_at), new Date(checkpoint.updated_at || checkpoint.created_at).toLocaleString()],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 border-y border-[#1e251f] divide-[#1e251f] sm:divide-x" data-testid="stat-strip">
      {stats.map(([label, value, exact], i) => (
        <div key={label} className={`py-4 sm:px-4 first:sm:pl-0 ${i === 4 ? 'col-span-2 sm:col-span-1' : ''}`} title={exact || ''}>
          <div className="font-mono text-[10px] text-[#666666] lowercase tracking-wide">{label}</div>
          <div className="font-mono text-xl sm:text-2xl text-white tabular-nums mt-1 truncate">{value}</div>
        </div>
      ))}
    </div>
  );
}

const LABELS = {
  width: 'width', bands: 'bands', model_dim: 'model width', token_dim: 'token width', expansion_dim: 'expansion',
  cortex_width: 'cortex width', cortex_depth: 'cortex depth', workspace_tokens: 'exact workspace',
  readout_window: 'pointer window', cortex_rate: 'cortex rate', source_gain: 'source gain',
  evidence_quantum: 'evidence quantum', decimation_stride_cap: 'max stride', traversal_gain: 'traversal gain',
  width_scaling: 'width scaling', input_representation: 'input', phi: 'base', trunk_depth: 'trunk depth',
};

function label(key) {
  return LABELS[key] || key.replace(/_/g, ' ');
}

function value(v) {
  if (typeof v === 'number') {
    if (Number.isInteger(v)) return v.toLocaleString();
    return Number(v.toPrecision(5)).toString();
  }
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  return v === '' ? '–' : String(v);
}

function Row({ k, v, href, title }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-[#141a15]" title={title || ''}>
      <span className="font-mono text-xs text-[#777777] lowercase shrink-0">{k}</span>
      {href
        ? <Link to={href} className="font-mono text-sm text-white text-right break-all hover:underline underline-offset-4 decoration-[#444444]">{v}</Link>
        : <span className="font-mono text-sm text-white text-right break-all">{v}</span>}
    </div>
  );
}

export function Specs({ checkpoint }) {
  const [all, setAll] = useState(false);
  const runtime = checkpoint.runtime_id ? checkpoint.runtime_id.replace(/^soma\./, '') : null;
  const isPhi = checkpoint.base && Math.abs(checkpoint.base - 1.618033988749895) < 0.001;
  const shape = [
    runtime && ['runtime', `${runtime} ${checkpoint.runtime_version || checkpoint.soma_version || ''}`.trim(), `/runtimes/${runtime}`],
    checkpoint.architecture && ['architecture', checkpoint.architecture],
    checkpoint.parameters && ['parameters', checkpoint.parameters.toLocaleString()],
    checkpoint.hidden_dim > 0 && ['width', checkpoint.hidden_dim.toLocaleString()],
    checkpoint.n_bands && ['bands', checkpoint.n_bands],
    ['depth', depthValue(checkpoint)],
    checkpoint.base && ['base', isPhi ? 'φ (1.618)' : checkpoint.base.toFixed(3)],
    checkpoint.licence && ['licence', checkpoint.licence],
    (checkpoint.data || []).length > 0 && ['read', checkpoint.data.map(d => `${d.label}${checkpoint.data.length > 1 ? ` ${Math.round((d.share || 0) * 100)}%` : ''}`).join(', ')],
    checkpoint.vocab_size && ['alphabet', `${checkpoint.vocab_size.toLocaleString()}${checkpoint.tokenizer_name ? ` · ${checkpoint.tokenizer_name}` : ''}`, null, checkpoint.tokenizer_sha256 ? `sha256 ${checkpoint.tokenizer_sha256}` : ''],
  ].filter(Boolean);

  // shown above, or retired (an earlier controller's settings, kept in checkpoints so they
  // load; a transformer body; the exact workspace when unused)
  const shown = new Set(['width', 'bands', 'hidden_dim', 'n_bands', 'phi', 'base', 'batch_min', 'batch_max',
    'metabolic_relaxation_tokens', 'surprise_response_tokens', 'residual_bands', 'body']);
  if (!checkpoint.config?.workspace_tokens) shown.add('workspace_tokens');
  let settings = Object.entries(checkpoint.config || {}).filter(([k]) => !shown.has(k)).sort(([a], [b]) => a.localeCompare(b));
  if (!settings.length) {
    settings = [
      ['batch', checkpoint.batch_size], ['controller', checkpoint.auto_mode],
      ['lr', checkpoint.lr_auto != null ? (checkpoint.lr_auto ? `auto · ${checkpoint.lr_base ?? '?'}` : checkpoint.lr_base) : null],
      ['decimation', checkpoint.decimation_auto != null ? (checkpoint.decimation_auto ? `auto · gain ${checkpoint.decimation_range ?? '?'}` : `band ${checkpoint.decimation_band ?? '?'}`) : null],
      ['grad clip', checkpoint.grad_clip], ['row norm', checkpoint.row_norm],
    ].filter(([, v]) => v !== null && v !== undefined);
  }
  const visible = all ? settings : settings.slice(0, 8);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8" data-testid="model-specs">
      <section>
        <h3 className="font-mono text-[11px] text-[#666666] lowercase tracking-wide mb-1">shape</h3>
        {shape.map(([k, v, href, title]) => <Row key={k} k={k} v={v} href={href} title={title} />)}
      </section>
      {settings.length > 0 && (
        <section>
          <h3 className="font-mono text-[11px] text-[#666666] lowercase tracking-wide mb-1">settings</h3>
          {visible.map(([k, v]) => <Row key={k} k={label(k)} v={value(v)} />)}
          {settings.length > 8 && (
            <button onClick={() => setAll(!all)} className="mt-2 font-mono text-xs text-[#777777] hover:text-white lowercase">
              {all ? 'fewer' : `all ${settings.length} settings`}
            </button>
          )}
        </section>
      )}
    </div>
  );
}
